import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  Calendar, CalendarRange, Clock, User, Mail, Phone, MapPin, Package, ChevronLeft, ChevronRight,
  Loader2, CheckCircle2, XCircle, FileText, TrendingUp, Home, Activity, Euro,
} from "lucide-react";
import { toast } from "sonner";
import { useSettings } from "../../lib/useSettings";

const STATUS_META = {
  pending_payment: { label: "Attente paiement", color: "amber" },
  pending: { label: "À valider", color: "orange" },
  confirmed: { label: "Confirmée", color: "emerald" },
  active: { label: "En cours", color: "sky" },
  completed: { label: "Terminée", color: "slate" },
  cancelled: { label: "Annulée", color: "red" },
};
const STATUS_STYLES = {
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  red: "bg-red-50 text-red-700 border-red-200",
};

function formatDateFr(iso, withTime = true) {
  try {
    const d = new Date(iso);
    const opts = withTime
      ? { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }
      : { weekday: "short", day: "2-digit", month: "short", year: "numeric" };
    return d.toLocaleString("fr-FR", opts);
  } catch { return iso; }
}

function money(cents, currency = "EUR") {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency }).format((cents || 0) / 100);
}

// ================== LIST PAGE ==================
export default function RentalsAdmin() {
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [notesOpen, setNotesOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = [];
      if (statusFilter) qs.push(`status=${statusFilter}`);
      if (productFilter) qs.push(`product_id=${productFilter}`);
      const q = qs.length ? `?${qs.join("&")}` : "";
      const [r, p] = await Promise.all([
        api.get(`/admin/rentals${q}`),
        api.get("/products").catch(() => ({ data: [] })),
      ]);
      setItems(r.data.items || []);
      const rentalProducts = (Array.isArray(p.data) ? p.data : (p.data?.items || [])).filter(x => x.is_rental);
      setProducts(rentalProducts);
    } catch (e) {
      toast.error("Impossible de charger les locations");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, productFilter]); // eslint-disable-line

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/rentals/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const saveNotes = async (id, admin_notes) => {
    try {
      await api.put(`/admin/rentals/${id}`, { admin_notes });
      toast.success("Note enregistrée");
      setNotesOpen(null);
      load();
    } catch (e) { toast.error("Erreur"); }
  };

  const currency = settings?.currency || "EUR";

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="rentals-admin">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Locations</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Réservations de durée — vélos, logements, machines…</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="rentals-filter-status"><SelectValue placeholder="Tous les statuts"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={productFilter || "all"} onValueChange={(v) => setProductFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="rentals-filter-product"><SelectValue placeholder="Tous les produits"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les produits</SelectItem>
              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </header>

      {loading ? (
        <Card className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--brand-sky)]"/></Card>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="rentals-empty">
          <CalendarRange className="w-10 h-10 mx-auto opacity-30 mb-3"/>
          <p className="font-semibold">Aucune location</p>
          <p className="text-sm mt-1">Activez le module location dans les paramètres et créez votre premier produit louable.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(b => {
            const meta = STATUS_META[b.status] || STATUS_META.pending;
            return (
              <Card key={b.id} className="p-5" data-testid={`rental-row-${b.id}`}>
                <div className="flex flex-wrap items-start gap-5">
                  {/* Product thumb */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-[hsl(var(--muted))] shrink-0">
                    {b.product_image ? <img src={b.product_image} alt={b.product_name} className="w-full h-full object-cover"/> : <Package className="w-6 h-6 m-5 opacity-40"/>}
                  </div>
                  {/* Main */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="font-display text-lg font-bold text-[color:var(--brand-navy)] truncate">{b.product_name}</div>
                      <Badge className={`border ${STATUS_STYLES[meta.color]} border`}>{meta.label}</Badge>
                      {b.unit_name && <span className="text-xs px-2 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">Unité: {b.unit_name}</span>}
                    </div>
                    <div className="mt-2 grid md:grid-cols-2 gap-2 text-sm text-[hsl(var(--muted-foreground))]">
                      <div className="flex items-center gap-2"><Calendar className="w-4 h-4"/>{formatDateFr(b.start_at)} <ChevronRight className="w-3 h-3"/> {formatDateFr(b.end_at)}</div>
                      <div className="flex items-center gap-2"><Clock className="w-4 h-4"/>{b.duration} {b.duration_unit === "hour" ? "h" : b.duration_unit === "night" ? "nuit(s)" : "jour(s)"}</div>
                      <div className="flex items-center gap-2"><User className="w-4 h-4"/>{b.customer_name}</div>
                      <div className="flex items-center gap-2"><Mail className="w-4 h-4"/>{b.customer_email}</div>
                      {b.customer_phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4"/>{b.customer_phone}</div>}
                      <div className="flex items-center gap-2"><Euro className="w-4 h-4"/>{money(b.total_price_cents, currency)} <span className="text-xs">({b.payment_mode === "deposit" ? `acompte ${money(b.deposit_cents, currency)}` : b.payment_mode})</span></div>
                    </div>
                    {b.customer_notes && <div className="mt-2 text-xs px-3 py-2 bg-amber-50 border border-amber-200 rounded text-amber-800"><strong>Note client :</strong> {b.customer_notes}</div>}
                    {notesOpen === b.id && (
                      <div className="mt-3 space-y-2">
                        <Textarea defaultValue={b.admin_notes || ""} id={`notes-${b.id}`} rows={3} placeholder="Notes internes (caution, accessoires, etc.)"/>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => saveNotes(b.id, document.getElementById(`notes-${b.id}`).value)} data-testid={`save-notes-${b.id}`}>Enregistrer</Button>
                          <Button size="sm" variant="outline" onClick={() => setNotesOpen(null)}>Annuler</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Actions */}
                  <div className="flex flex-col gap-2 min-w-[180px]">
                    <Select value={b.status} onValueChange={(v) => setStatus(b.id, v)}>
                      <SelectTrigger className="h-9" data-testid={`status-select-${b.id}`}><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => setNotesOpen(notesOpen === b.id ? null : b.id)} data-testid={`notes-btn-${b.id}`}>
                      <FileText className="w-4 h-4 mr-2"/>Notes
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
